import moengage from '@moengage/web-sdk';
import { UAParser } from 'ua-parser-js';

import { logtoCookies } from './cookies';
import { getRuntimeConfig } from './config';
import { normalizeAppName, type AppName } from './app-name';

const LOG_PREFIX = '[MoEngage Event]';

// Cache for application name to avoid repeated API calls
let cachedAppName: AppName | undefined;

/**
 * Fetches and caches the application name for event tracking
 * Called once on module load to pre-populate the cache
 */
const initAppNameCache = async (): Promise<void> => {
  const { appId } = logtoCookies;
  console.log(`${LOG_PREFIX} initAppNameCache: appId from cookies = "${appId}"`);

  if (!appId) {
    console.log(`${LOG_PREFIX} initAppNameCache: No appId, skipping cache initialization`);
    return;
  }

  try {
    console.log(`${LOG_PREFIX} initAppNameCache: Getting runtime config...`);
    const config = getRuntimeConfig();
    console.log(`${LOG_PREFIX} initAppNameCache: VITE_ONE_MEMBER_API_ENDPOINT = "${config.VITE_ONE_MEMBER_API_ENDPOINT}"`);
    const url = `${config.VITE_ONE_MEMBER_API_ENDPOINT}/api/applications/${appId}`;
    console.log(`${LOG_PREFIX} initAppNameCache: Fetching from ${url}`);

    const response = await fetch(url);

    if (!response.ok) {
      console.log(`${LOG_PREFIX} initAppNameCache: Failed to fetch, status = ${response.status}`);
      return;
    }

    const data = await response.json();
    console.log(`${LOG_PREFIX} initAppNameCache: API response =`, data);

    if (data && typeof data === 'object' && 'name' in data && typeof data.name === 'string') {
      const rawName = data.name;
      cachedAppName = normalizeAppName(rawName);
      console.log(
        `${LOG_PREFIX} initAppNameCache: ✅ Cached app name: "${rawName}" → "${cachedAppName}"`
      );
    }
  } catch (error) {
    console.error(`${LOG_PREFIX} initAppNameCache: Error =`, error);
  }
};

// Pre-fetch app name on module load
void initAppNameCache();

/**
 * Gets the cached app name synchronously
 * Returns undefined if not yet fetched
 */
const getCachedAppName = (): AppName | undefined => cachedAppName;

const toSnake = (input: string) => input.replace(/[^a-zA-Z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').toLowerCase();

export const pathNameLowercase = (pathname: string) => {
  if (!pathname || pathname === '/') {
    return 'todo';
  }

  return pathname.toLowerCase();
};

export const buildFlowFromPath = (pathname: string): string => {
  if (!pathname || pathname === '/') {
    return 'todo';
  }

  const path = pathname.toLowerCase();

  // Login flow (includes sign-in, register, password reset)
  if (path.startsWith('/sign-in')) return 'login';
  if (path.startsWith('/sign-in/password')) return 'login';
  if (path.startsWith('/direct/')) return 'login';
  if (path.startsWith('/register')) return 'login';
  if (path.startsWith('/register/password')) return 'login';
  if (path.startsWith('/one-time-token')) return 'login';
  if (path.startsWith('/forgot-password')) return 'login';
  if (path.startsWith('/forgot-password/reset')) return 'login';
  if (path.startsWith('/reset-password')) return 'login';
  if (path.startsWith('/identifier-sign-in')) return 'login';
  if (path.startsWith('/identifier-register')) return 'login';

  // On-boarding flow (post-login/register steps)
  if (path.startsWith('/continue/')) return 'on_boarding';
  if (path === '/mfa-binding' || path.startsWith('/mfa-binding/')) return 'on_boarding';
  
  // MFA verification is part of login flow
  if (path === '/mfa-verification' || path.startsWith('/mfa-verification/')) return 'login';
  
  // Authentication methods (part of login flow)
  if (path === '/single-sign-on' || path.startsWith('/single-sign-on/')) return 'login';
  if (path === '/social' || path.startsWith('/social/')) return 'login';
  
  // Account recovery and switching (part of login flow)
  if (path.startsWith('/recover')) return 'login';
  if (path.startsWith('/switch-account')) return 'login';
  if (path.startsWith('/springboard')) return 'login';
  if (path.startsWith('/unknown-session')) return 'login_error';
  
  // Post-authentication (on-boarding)
  if (path.startsWith('/consent')) return 'on_boarding';

  // Fallback to snake-cased path
  return toSnake(pathname);
};

export const buildScreenNameFromPath = (pathname: string): string => {
  if (!pathname || pathname === '/') {
    return 'home';
  }

  const path = pathname.toLowerCase();

  // Authentication screens - matching Figma flow chart
  if (path.startsWith('/sign-in/password')) return 'login_with_password';
  if (path.startsWith('/sign-in')) return 'login';
  if (path.startsWith('/identifier-sign-in')) return 'login';
  if (path.startsWith('/register/password')) return 'set_password_optional';
  if (path.startsWith('/register')) return 'register';
  if (path.startsWith('/identifier-register')) return 'register';
  if (path.startsWith('/one-time-token')) return 'otp_verification';
  if (path.startsWith('/forgot-password/reset')) return 'reset_password';
  if (path.startsWith('/forgot-password')) return 'forgot_password';
  if (path.startsWith('/reset-password')) return 'reset_password';

  // Verification code screens (OTP)
  if (path.includes('/verification-code')) return 'otp_verification';

  // Methods
  if (path.startsWith('/single-sign-on')) return 'single_sign_on';
  if (path.startsWith('/social')) return 'social';

  // MFA
  if (path.startsWith('/mfa-verification')) return 'mfa_verification';
  if (path.startsWith('/mfa-binding')) return 'mfa_binding';

  // Account utilities
  if (path.startsWith('/recover')) return 'recover';
  if (path.startsWith('/switch-account')) return 'switch_account';
  if (path.startsWith('/springboard')) return 'springboard';
  if (path.startsWith('/unknown-session')) return 'unknown_session';

  // Continue/onboarding
  if (path.startsWith('/continue/')) {
    const rest = path.replace('/continue/', '');
    const segment = rest.split(/[/?#]/)[0] || '';
    if (segment) {
      return `continue_${toSnake(segment)}`;
    }
    return 'continue';
  }

  // Consent
  if (path.startsWith('/consent')) return 'consent';

  // Default to snake-cased full path
  return toSnake(pathname);
};

const MOBILE_BREAKPOINT = 768;

function detectDeviceType(): 'mobile' | 'desktop' {
  return window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop';
}

function detectOS(parser: UAParser.IResult): 'windows' | 'macos' | 'linux' | 'android' | 'ios' | 'unknown' {
  const osName = parser.os?.name?.toLowerCase() ?? '';

  if (osName.includes('windows')) return 'windows';
  if (osName.includes('mac')) return 'macos';
  if (osName.includes('linux')) return 'linux';
  if (osName.includes('android')) return 'android';
  if (osName.includes('ios') || osName.includes('iphone') || osName.includes('ipad')) return 'ios';

  return 'unknown';
}

function detectBrowser(parser: UAParser.IResult): 'chrome' | 'safari' | 'firefox' | 'edge' | 'opera' | 'unknown' {
  const browserName = parser.browser.name?.toLowerCase() ?? '';

  if (browserName.includes('chrome')) return 'chrome';
  if (browserName.includes('safari')) return 'safari';
  if (browserName.includes('firefox')) return 'firefox';
  if (browserName.includes('edge')) return 'edge';
  if (browserName.includes('opera')) return 'opera';

  return 'unknown';
}

export const getDeviceInfo = () => {
  try {
    const parser = new UAParser();
    const deviceType = detectDeviceType();

    return {
      deviceType,
      platform: deviceType,
      os: detectOS(parser.getResult()),
      browser: detectBrowser(parser.getResult()),
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  } catch {
    return {
      deviceType: 'unknown' as const,
      platform: 'unknown' as const,
      os: 'unknown' as const,
      browser: 'unknown' as const,
      screenWidth: 0,
      screenHeight: 0,
      viewportWidth: 0,
      viewportHeight: 0,
    };
  }
};

export const trackEvent = (
  component: string,
  action: 'clicked' | 'submitted' | 'closed' | 'skipped' | 'selected' | 'opened' | 'closed_menu',
  trackingInfo: {
    eventId: string; // Event Name from mapping; may include {current_screen}
  },
  payload?: Record<string, unknown>
) => {
  try {
    const path = window.location.pathname;
    const flow = buildFlowFromPath(path);
    const deviceInfo = getDeviceInfo();
    const screenName = buildScreenNameFromPath(path);
    const app = getCachedAppName();

    const eventPayload = {
      ...deviceInfo,
      ...payload,
      ...trackingInfo,
      flow,
      pathname: path,
      screenName,
      action,
      component,
      ...(app && { app }),
    };

    console.log(`${LOG_PREFIX} trackEvent: eventId="${trackingInfo.eventId}", app="${app}"`, eventPayload);
    moengage.track_event(trackingInfo.eventId, eventPayload);
  } catch (error) {
    console.log('Error tracking event:', error);
    // no-op
  }
};

/**
 * Brief delay to let MoEngage HTTP requests complete before the page is
 * torn down by a redirect.  On Android the system browser / Custom Tab is
 * destroyed as soon as the deep-link callback fires, killing any in-flight
 * XHR.  The delay only runs once (at registration) so the UX cost is
 * negligible.
 */
export const waitForMoEngageFlush = (): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, 1000);
  });
};

/**
 * Sends an analytics event via navigator.sendBeacon() to the backend proxy.
 * sendBeacon is guaranteed to deliver data even during page unload/navigation,
 * which is critical on Android where Chrome Custom Tab destroys in-flight XHR
 * when the deep-link callback fires after registration.
 */
const sendBeaconEvent = (
  eventName: string,
  eventPayload: Record<string, unknown>,
  userId?: string,
  userAttributes?: Record<string, unknown>
): void => {
  try {
    const config = getRuntimeConfig();
    const url = `${config.VITE_ONE_MEMBER_API_ENDPOINT}/api/analytics/events`;
    const body = JSON.stringify({ eventName, eventPayload, userId, userAttributes });
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  } catch {
    // no-op
  }
};

/**
 * Fires a registration_completed event via navigator.sendBeacon() to the
 * backend proxy, which forwards it to MoEngage's Data API server-side.
 *
 * Why sendBeacon instead of moengage.track_event()?
 * On Android the auth flow runs in a Chrome Custom Tab. When
 * window.location.replace() fires after registration, Chrome unloads
 * the page and kills all pending XHR — including MoEngage SDK's internal
 * event dispatch. sendBeacon is guaranteed to deliver even during page
 * teardown, solving the Android-only event loss.
 *
 * Called from registration paths:
 * - SSO registration (use-single-sign-on-listener.ts)
 * - Extra profile submission during registration (use-set-extra-profile.ts)
 * - Password registration (RegisterPassword)
 */
export const trackRegistrationCompleted = (
  signUpFromApp?: string,
  userId?: string,
  userAttributes?: Record<string, unknown>
) => {
  try {
    const path = window.location.pathname;
    const flow = buildFlowFromPath(path);
    const screenName = buildScreenNameFromPath(path);
    const deviceInfo = getDeviceInfo();
    const app = getCachedAppName();

    const eventPayload = {
      ...deviceInfo,
      flow,
      pathname: path,
      screenName,
      ...(app && { app }),
      ...(signUpFromApp && { signUpFromApp }),
      ...(userId && { userId }),
    };

    console.log(`${LOG_PREFIX} trackRegistrationCompleted via sendBeacon: signUpFromApp="${signUpFromApp}", userId="${userId}", app="${app}"`, eventPayload);
    sendBeaconEvent('registration_completed', eventPayload, userId, userAttributes);
  } catch {
    // no-op — must never block the redirect
  }
};

export const trackPageView = (pathname?: string, additionalData?: Record<string, unknown>) => {
  try {
    const path = pathname || window.location.pathname;
    if (path.includes('callback')) {
      return;
    }
    const flow = buildFlowFromPath(path);
    const screenName = buildScreenNameFromPath(path);
    const deviceInfo = getDeviceInfo();
    const app = getCachedAppName();

    const pageViewPayload = {
      flow,
      pathname: path,
      screenName,
      ...deviceInfo,
      ...additionalData,
      ...(app && { app }),
    };

    console.log(`${LOG_PREFIX} trackPageView: path="${path}", app="${app}"`, pageViewPayload);
    moengage.track_event('page_view', pageViewPayload);
  } catch (error) {
    console.log('Error tracking page view:', error);
    // no-op
  }
};


